import { DataService } from "@/services/data";
import { readsFormValues, useDynamicOptions } from "@/hooks/use-dynamic-options";
import { actionFieldErrors, buildZodSchema, buildDefaultValues } from "@/utils/dynamic-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Form } from "../ui/form";
import { ActionModalProps, ActionResolution } from "./types2";
import { FormError } from "../ui/form-error";
import { MarkdownRenderer } from "../ui/markdown-renderer";
import { useNavigate } from "react-router-dom";
import { ActionCheckResult } from "@/types/crm/action";
import { Loader2 } from "lucide-react";
import { useDebounce } from "@/utils/misc";
import { DynamicFormField } from "./dynamic-form-field";
import { resetFieldMemory } from "@/utils/field-memory";
import { hasFormConditions, keepVisibleValues, optionAttributeReader, resolveConditionalFields } from "@/utils/form-conditions";
import { effectiveOptionMap } from "@/utils/field-options";
import type { FieldDataType } from "@/types/dynamic-detail";
import type { FilterTree } from "./filter-tree/types";

type ActionModalExtendedProps = ActionModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    records: string[];
    is_all?: boolean;
    endpointUrl: string;
    action_scope: "list" | "detail";
    drqQuery?: string | null;
    /** Search-bar value — must be sent with is_all so the action scopes to the filtered list */
    searchValue?: string;
    /** Visible column fields required by the backend to apply ?search= */
    searchFields?: string[];
    /** Advanced filters of the list — sent with is_all so the action scopes to those rows */
    filterTree?: FilterTree | null;
    /** Called after the action completed successfully (e.g. to refresh the list) */
    onSuccess?: () => void;
    /**
     * Opens the action a refusal offers (`resolutions`), with the values it carries over what was typed here (the
     * motif is not typed twice). Without it, the offers are not shown.
     */
    onResolution?: (resolution: ActionResolution, typed: Record<string, unknown>) => void;
};

export function ActionModal({
    open,
    onOpenChange,
    action_title,
    action_description,
    action_api_name,
    redirect_url,
    redirect_target,
    button_label,
    fields,
    records,
    is_all = false,
    endpointUrl,
    action_scope,
    sync,
    checks,
    drqQuery,
    searchValue,
    searchFields,
    filterTree,
    onSuccess,
    onResolution,
    preflight_context
}: ActionModalExtendedProps) {
    const navigate = useNavigate();
    const conditional = hasFormConditions(fields);
    // As `DynamicObjectForm` (G1): the schema follows the fields the current values leave in the form,
    // so a field hidden by its `visible_when` is never validated (nor blocks « Valider ») and one whose
    // `required_when` holds is required. Read through a ref: the resolver is set once.
    const conditionsRef = useRef<{ fields: FieldDataType[]; read?: ReturnType<typeof optionAttributeReader> }>({ fields });
    const fieldsFor = useCallback(
        (values: Record<string, unknown>): FieldDataType[] =>
            conditional ? resolveConditionalFields(conditionsRef.current.fields, values, conditionsRef.current.read) : conditionsRef.current.fields,
        [conditional]
    );
    const listFilterTree = useMemo(
        () => (is_all && filterTree && filterTree.root.children.length > 0 ? filterTree : null),
        [is_all, filterTree]
    );
    const form: UseFormReturn<Record<string, unknown>> = useForm<Record<string, unknown>>({
        resolver: (values, resolverContext, options) => zodResolver(buildZodSchema(fieldsFor(values)))(values, resolverContext, options),
        mode: "onChange",
        defaultValues: buildDefaultValues(fields)
    });

    // Watched only when an option list or a field's `visible_when` depends on another field of the form.
    const editedValues = readsFormValues(fields) || conditional ? form.watch() : undefined;
    const { dynamicOptions, loadingOptions, failedOptions } = useDynamicOptions(fields, undefined, editedValues);
    // Effective options (`resolveFieldOptions`: a served list wins over a fetched one), read by the fields,
    // the rows that follow a field and the conditions' option attributes.
    const fieldOptions = useMemo(() => effectiveOptionMap(fields, { dynamicOptions }), [fields, dynamicOptions]);
    const readAttribute = useMemo(() => optionAttributeReader(fields, fieldOptions), [fields, fieldOptions]);
    conditionsRef.current = { fields, read: readAttribute };
    // A field whose `visible_when` does not hold is neither rendered nor submitted (« Modalité de
    // paiement » of the customer type chosen, say).
    // `isValid` (« Valider ») is computed by the resolver on every change (mode `onChange`), so it
    // follows a condition that flips.
    const shownFields = conditional ? resolveConditionalFields(fields, editedValues ?? {}, readAttribute) : fields;
    // A condition reading an option attribute can flip when its list arrives, with no value edited: the
    // resolver runs again then, so « Valider » follows the fields actually shown.
    const shownKey = conditional ? shownFields.map(field => `${field.field_name}:${Boolean(field.required)}`).join(",") : "";
    useEffect(() => {
        if (open && shownKey) void form.trigger();
    }, [shownKey]); // eslint-disable-line react-hooks/exhaustive-deps
    const [submitting, setSubmitting] = useState(false);
    const debounceSubmitting = useDebounce(submitting, 1000);

    // Ref pour éviter les appels multiples d'onSubmit
    const hasExecutedRef = useRef(false);

    // Gestion des erreurs de champs backend
    const [backendFieldErrors, setBackendFieldErrors] = useState<Record<string, string>>({});
    const [backendErrors, setBackendErrors] = useState<string | null>(null);
    // The ways out the server offers with its refusal (another action of the record, prefilled).
    const [resolutions, setResolutions] = useState<ActionResolution[]>([]);

    // The server's messages under the fields they name; a message naming no field of the dialog stays readable
    // in the banner instead of being dropped.
    const showServerErrors = useCallback(
        (body: Parameters<typeof actionFieldErrors>[0], message: string) => {
            const inline = actionFieldErrors(body);
            const names = new Set(fields.map(field => field.field_name));
            const shown = Object.fromEntries(Object.entries(inline).filter(([name]) => names.has(name)));
            const elsewhere = Object.entries(inline)
                .filter(([name]) => !names.has(name))
                .map(([, text]) => text);
            if (Object.keys(shown).length > 0) setBackendFieldErrors(shown);
            if (Object.keys(shown).length === 0 || elsewhere.length > 0) setBackendErrors(elsewhere.length > 0 ? elsewhere.join(" ") : message);
        },
        [fields]
    );

    useEffect(() => {
        if (!open) return;
        form.reset(buildDefaultValues(fields));
        resetFieldMemory(form);
        setBackendErrors(null);
        setBackendFieldErrors({});
        setResolutions([]);
        // Reset only when the dialog opens or the action changes — not on every
        // `fields` identity change, which would wipe values while the user types.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, action_api_name]);

    // Pré-check d'éligibilité de l'action (GET /actions/)
    const [precheckResults, setPrecheckResults] = useState<ActionCheckResult[] | null>(null);
    const [precheckLoading, setPrecheckLoading] = useState(false);

    const buildActionQuery = useCallback(
        (includePrecheckParams = false) => {
            const query: Record<string, string | string[] | boolean | undefined> = {};
            if (includePrecheckParams) {
                query.action_api_name = action_api_name;
                query.action_scope = action_scope;
                query.ids = records;
                query.is_all = is_all || undefined;
            }
            if (is_all) {
                if (drqQuery) {
                    query.query = drqQuery;
                }
                // Mirror list-fetch params so is_all respects the search bar
                const trimmedSearch = searchValue?.trim();
                if (trimmedSearch) {
                    query.search = trimmedSearch;
                    const fieldsForSearch = searchFields?.length ? [...new Set(["id", ...searchFields])] : ["id"];
                    query.fields = fieldsForSearch.join(",");
                }
            }
            return query;
        },
        [action_api_name, action_scope, drqQuery, is_all, records, searchFields, searchValue]
    );

    const onSubmit = useCallback(
        async (values: Record<string, unknown>) => {
            setBackendErrors(null);
            setBackendFieldErrors({});
            setResolutions([]);
            setSubmitting(true);
            try {
                // Appel API générique sans action_scope
                const response = await DataService.client
                    .url(endpointUrl + "actions/")
                    .query(buildActionQuery())
                    .post({
                        action_api_name: action_api_name,
                        action_scope: action_scope,
                        ids: records.length > 0 ? records : undefined,
                        is_all: is_all || undefined,
                        fields: conditional ? keepVisibleValues(fieldsFor(values), values) : values,
                        preflight_context,
                        ...(listFilterTree ? { filter_tree: listFilterTree } : {})
                    })
                    .json<{
                        success: boolean;
                        redirect_url: string;
                        redirect_target?: string;
                        target?: string;
                        error?: string;
                        message?: string | null;
                        missing_fields?: string[];
                        field_errors?: Record<string, string[] | string>;
                        resolutions?: ActionResolution[];
                        sync?: boolean;
                    }>();
                if (response && response.success) {
                    // The server queues a sync action when it is too large or the user already has runs in flight.
                    const ranSync = response.sync ?? sync;
                    if (fields.length > 0 || !ranSync) {
                        if (ranSync) {
                            toast.success(response.message || `Action "${action_title}" has been executed`);
                        } else {
                            toast.success(response.message || `Action "${action_title}" has been queued`);
                        }
                    }

                    if (response.redirect_url) {
                        if (response.redirect_target === "_blank") {
                            window.open(response.redirect_url, "_blank");
                        } else if (/^https?:\/\//.test(response.redirect_url)) {
                            window.location.href = response.redirect_url; // Externe
                        } else {
                            navigate(response.redirect_url); // Interne
                        }
                    } else {
                        navigate(".");
                    }

                    onSuccess?.();
                    onOpenChange(false);
                    form.reset();
                } else {
                    toast.error(response?.error || "Error executing the action");
                    if (fields.length > 0) {
                        // The server's messages under their field (`field_errors`, else the generic one for
                        // `missing_fields`); anything else in a readable banner.
                        showServerErrors(response, response?.error || "Error executing the action");
                        setResolutions(response?.resolutions ?? []);
                    } else {
                        onOpenChange(false);
                    }
                }
            } catch (e: unknown) {
                let message = "Error executing the action";
                if (e instanceof Error) {
                    try {
                        const body = JSON.parse(e.message) as {
                            error?: string;
                            field_errors?: Record<string, string[] | string>;
                            missing_fields?: string[];
                            resolutions?: ActionResolution[];
                        };
                        message = body.error || message;
                        // The server's own checks (a `required_when` that holds, an option no longer offered, a
                        // financeur rule) are shown under their field, with the ways out the refusal offers.
                        if (fields.length > 0) {
                            showServerErrors(body, message);
                            setResolutions(body.resolutions ?? []);
                        }
                    } catch {
                        message = e.message || message;
                        // Readable: the server's `error`, never the raw body.
                        if (fields.length > 0) setBackendErrors(message);
                    }
                }
                toast.error(message);
                if (fields.length === 0) {
                    onOpenChange(false);
                }
            } finally {
                setSubmitting(false);
            }
        },
        [action_api_name, action_scope, action_title, buildActionQuery, conditional, endpointUrl, fields, fieldsFor, form, is_all, listFilterTree, navigate, onOpenChange, onSuccess, preflight_context, records, showServerErrors, sync]
    );

    useEffect(() => {
        if (checks) {
            if (!open) {
                setPrecheckResults(null);
                hasExecutedRef.current = false; // Reset la ref quand le modal se ferme
                return;
            }
            setPrecheckLoading(true);
            const request = DataService.client.url(endpointUrl + "actions/").query(buildActionQuery(true));
            // Same body as the list: the tree is too long for the query string.
            const pending = listFilterTree ? request.post({ precheck: true, filter_tree: listFilterTree }) : request.get();
            pending
                .json<ActionCheckResult[]>()
                .then(res => {
                    setPrecheckResults(res);
                })
                .catch(() => setPrecheckResults([{ error: { type: "warning", message: "Unable to verify action eligibility right now." }, helper_text: null }]))
                .finally(() => setPrecheckLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, action_api_name, endpointUrl, action_scope, checks, is_all, drqQuery, searchValue, searchFields, listFilterTree]);

    useEffect(() => {
        if (!open) {
            hasExecutedRef.current = false; // Reset la ref quand le modal se ferme
            return;
        }
        if (open && !submitting && fields.length == 0 && !hasExecutedRef.current) {
            hasExecutedRef.current = true; // Marquer comme exécuté
            if (redirect_url && redirect_url.includes("$recordIds")) {
                // If the redirect_url contains $recordIds, we need to call the backend to get the redirect_url
                onSubmit({});
            } else if (redirect_url) {
                if (redirect_target === "_blank") {
                    window.open(redirect_url, "_blank");
                } else if (/^https?:\/\//.test(redirect_url)) {
                    window.location.href = redirect_url; // Externe
                } else {
                    navigate(redirect_url); // Interne
                }
                onOpenChange(false);
            } else {
                onSubmit({});
            }
        }
    }, [open, submitting, fields.length, redirect_url, redirect_target, navigate, onOpenChange, onSubmit]);

    // If no fields, show a loading state after 1 second of submitting to prevent a modal flash
    if (fields.length == 0) {
        if (debounceSubmitting) {
            return (
                <Dialog open={open} onOpenChange={onOpenChange}>
                    <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                        <DialogHeader>
                            <DialogTitle>{action_title}</DialogTitle>
                            <DialogDescription>{<MarkdownRenderer content={action_description} />}</DialogDescription>
                        </DialogHeader>
                        <div className="flex h-[350px] w-full items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin" />
                        </div>
                    </DialogContent>
                </Dialog>
            );
        }
        return null;
    }

    // If fields, show the form
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col overflow-hidden">
                <DialogHeader className="shrink-0">
                    <DialogTitle>{action_title}</DialogTitle>
                    <DialogDescription>{<MarkdownRenderer content={action_description} />}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
                        <div className="relative -m-1 min-h-0 flex-1 overflow-y-auto overscroll-contain p-1">
                            {precheckResults &&
                                precheckResults.map((result, index) => (
                                    <div key={index}>
                                        {result.error && <FormError errorMessage={`${result.error.type.toUpperCase()}: ${result.error.message}`} />}
                                        {result.helper_text && <p className="mt-2 text-sm text-muted-foreground">{result.helper_text}</p>}
                                    </div>
                                ))}
                            {backendErrors && <FormError errorMessage={backendErrors} />}
                            {shownFields.length > 0 && (
                                <div className="grid grid-cols-2 gap-4 py-4">
                                    {shownFields.map(field => (
                                        <DynamicFormField
                                            key={field.field_name}
                                            field={field}
                                            form={form}
                                            dynamicOptions={fieldOptions}
                                            loadingOptions={loadingOptions}
                                            failedOptions={failedOptions}
                                            backendFieldErrors={backendFieldErrors}
                                            contextName={action_api_name}
                                            readAttribute={readAttribute}
                                            siblingFields={fields}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {onResolution && resolutions.length > 0 && (
                            <div className="mt-3 flex shrink-0 flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-3">
                                {resolutions.map(resolution => (
                                    <div key={`${resolution.action}:${resolution.label}`} className="flex items-start justify-between gap-3">
                                        <div className="text-sm text-amber-900">
                                            <p className="font-medium">{resolution.label}</p>
                                            {resolution.description && <p className="text-xs">{resolution.description}</p>}
                                        </div>
                                        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={() => onResolution(resolution, form.getValues())}>
                                            Choisir
                                        </Button>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="mt-4 flex shrink-0 justify-end border-t pt-4">
                            <Button
                                type="submit"
                                variant="default"
                                disabled={!form.formState.isValid || submitting || precheckLoading || precheckResults?.some(result => result.error?.type === "error")}
                            >
                                {submitting ? "Submitting..." : precheckLoading ? "Checking..." : button_label || (fields.length > 0 ? "Validate" : "Continue")}
                            </Button>
                        </div>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
